<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useOvvaBackend,
  getOvvaPat,
  setOvvaPat,
} from 'dashboard/composables/useOvvaBackend';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ChannelMessengerCard from './ChannelMessengerCard.vue';
import ChannelWebsiteCard from './ChannelWebsiteCard.vue';

// Вкладка «Каналы»: точное воспроизведение sec-client-channels из
// platform-backend/views/embed.ejs (разметка ~строки 790-826, логика —
// unlockChannels/lockChannels/loadClientChannels/loadChannelAudit
// ~строки 2456-2690). Двойной замок: сначала личный Access Token (PAT)
// оператора в localStorage, затем пробный GET /clients/:id/channels —
// успешный ответ массивом и есть доказательство прав (SuperAdminPatGuard
// на бэкенде), отдельной /check-ручки для этого нет.
const props = defineProps({
  sources: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const { callBackend } = useOvvaBackend();

const clientId = ref(null);
const isUnlocked = ref(false);
const isProbing = ref(false);
const lockNote = ref('');

const showPatPrompt = ref(false);
const patInputValue = ref('');

const scopeSourceId = ref('');
const channels = ref([]);
const isLoadingChannels = ref(false);

const auditRows = ref([]);
const isLoadingAudit = ref(false);

const scopeOptions = computed(() => [
  { value: '', label: t('SOURCE_MGMT.CHANNELS.SCOPE.COMPANY') },
  ...props.sources
    .filter(s => s.isActive)
    .map(s => ({
      value: String(s.id),
      label: t('SOURCE_MGMT.CHANNELS.SCOPE.BRAND_PREFIX', {
        name: s.name || s.code || `#${s.id}`,
      }),
    })),
]);

const messengerChannels = computed(() =>
  channels.value.filter(c => c.kind !== 'website')
);
const websiteChannel = computed(() =>
  channels.value.find(c => c.kind === 'website')
);

const resolveClientId = async () => {
  if (clientId.value) return clientId.value;
  const clients = await callBackend('/clients');
  const id = Array.isArray(clients) && clients.length ? clients[0].id : null;
  if (!id) {
    throw new Error(t('SOURCE_MGMT.CHANNELS.API.CLIENT_ID_ERROR'));
  }
  clientId.value = id;
  return id;
};

const loadAudit = async () => {
  isLoadingAudit.value = true;
  try {
    const rows = await callBackend(`/clients/${clientId.value}/channels/audit`);
    auditRows.value = Array.isArray(rows) ? rows : [];
  } catch (error) {
    auditRows.value = [];
  } finally {
    isLoadingAudit.value = false;
  }
};

const resetChannelsState = () => {
  channels.value = [];
  auditRows.value = [];
};

// «Закрыть раздел» — это НЕ логаут: PAT в localStorage остаётся, просто
// прячем форму и чистим то, что было загружено под замком.
const lockChannels = () => {
  isUnlocked.value = false;
  lockNote.value = '';
  resetChannelsState();
};

const loadChannels = async () => {
  if (!isUnlocked.value) return;
  isLoadingChannels.value = true;
  try {
    const query = scopeSourceId.value
      ? `?sourceId=${encodeURIComponent(scopeSourceId.value)}`
      : '';
    const data = await callBackend(
      `/clients/${clientId.value}/channels${query}`
    );
    if (!Array.isArray(data)) {
      const why = data?.message || t('SOURCE_MGMT.CHANNELS.API.LOAD_ERROR');
      lockChannels();
      lockNote.value = why;
      useAlert(why);
      return;
    }
    channels.value = data;
    await loadAudit();
  } catch (error) {
    const why = error.message || t('SOURCE_MGMT.CHANNELS.API.LOAD_ERROR');
    lockChannels();
    lockNote.value = why;
    useAlert(why);
  } finally {
    isLoadingChannels.value = false;
  }
};

const unlockChannels = async () => {
  isProbing.value = true;
  lockNote.value = '';
  try {
    await resolveClientId();
    // Права проверяем до раскрытия — сам факт, что ответ пришёл массивом,
    // и есть доказательство прав, отдельного /check не вызываем.
    const probe = await callBackend(`/clients/${clientId.value}/channels`);
    if (!Array.isArray(probe)) {
      const why = probe?.message || t('SOURCE_MGMT.CHANNELS.API.RIGHTS_ERROR');
      lockNote.value = why;
      useAlert(why);
      return;
    }
    isUnlocked.value = true;
    await loadChannels();
  } catch (error) {
    lockNote.value =
      error.message || t('SOURCE_MGMT.CHANNELS.API.RIGHTS_ERROR');
    useAlert(lockNote.value);
  } finally {
    isProbing.value = false;
  }
};

const openChannels = async () => {
  if (!getOvvaPat()) {
    showPatPrompt.value = true;
    return;
  }
  await unlockChannels();
};

const connectPat = async () => {
  const value = patInputValue.value.trim();
  if (!value) {
    useAlert(t('SOURCE_MGMT.CHANNELS.PAT.REQUIRED'));
    return;
  }
  setOvvaPat(value);
  patInputValue.value = '';
  showPatPrompt.value = false;
  await unlockChannels();
};

const cancelPatPrompt = () => {
  showPatPrompt.value = false;
  patInputValue.value = '';
};

const onScopeChange = value => {
  scopeSourceId.value = value;
  loadChannels();
};

const formatAuditDate = value => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <div class="flex flex-col gap-4 max-w-3xl">
    <p class="text-body-main text-n-slate-11 m-0">
      {{ t('SOURCE_MGMT.CHANNELS.DESCRIPTION') }}
    </p>

    <div v-if="!isUnlocked" class="flex flex-col gap-3">
      <div v-if="lockNote" class="text-body-main text-n-ruby-11">
        {{ lockNote }}
      </div>

      <div v-if="showPatPrompt" class="flex flex-col gap-2 max-w-sm">
        <Input
          v-model="patInputValue"
          autocomplete="new-password"
          type="password"
          :label="t('SOURCE_MGMT.CHANNELS.PAT.LABEL')"
          :placeholder="t('SOURCE_MGMT.CHANNELS.PAT.PLACEHOLDER')"
          :message="t('SOURCE_MGMT.CHANNELS.PAT.HINT')"
          @keyup.enter="connectPat"
        />
        <div class="flex items-center gap-2">
          <NextButton
            size="sm"
            :label="t('SOURCE_MGMT.CHANNELS.PAT.CONNECT')"
            @click="connectPat"
          />
          <NextButton
            variant="faded"
            color="slate"
            size="sm"
            :label="t('SOURCE_MGMT.CHANNELS.PAT.CANCEL')"
            @click="cancelPatPrompt"
          />
        </div>
      </div>
      <NextButton
        v-else
        size="sm"
        class="w-fit"
        :label="t('SOURCE_MGMT.CHANNELS.OPEN_BUTTON')"
        :is-loading="isProbing"
        @click="openChannels"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('SOURCE_MGMT.CHANNELS.SCOPE.LABEL') }}
        </span>
        <Select
          :model-value="scopeSourceId"
          :options="scopeOptions"
          @update:model-value="onScopeChange"
        />
        <span class="text-label-small text-n-slate-11">
          {{ t('SOURCE_MGMT.CHANNELS.SCOPE.HINT') }}
        </span>
      </div>

      <NextButton
        variant="faded"
        color="slate"
        size="sm"
        class="w-fit"
        :label="t('SOURCE_MGMT.CHANNELS.CLOSE_BUTTON')"
        @click="lockChannels"
      />

      <div v-if="isLoadingChannels" class="text-body-main text-n-slate-11">
        {{ t('SOURCE_MGMT.CHANNELS.LOADING') }}
      </div>
      <div v-else class="flex flex-col gap-3">
        <ChannelMessengerCard
          v-for="c in messengerChannels"
          :key="c.channel"
          :channel="c"
          :client-id="clientId"
          :scope-source-id="scopeSourceId"
          @saved="loadChannels"
        />
        <ChannelWebsiteCard v-if="websiteChannel" :channel="websiteChannel" />
      </div>

      <div class="flex flex-col gap-2">
        <h4 class="text-heading-3 text-n-slate-12 m-0">
          {{ t('SOURCE_MGMT.CHANNELS.AUDIT.TITLE') }}
        </h4>
        <span class="text-label-small text-n-slate-11">
          {{ t('SOURCE_MGMT.CHANNELS.AUDIT.HINT') }}
        </span>
        <div
          v-if="!isLoadingAudit && !auditRows.length"
          class="text-body-main text-n-slate-11"
        >
          {{ t('SOURCE_MGMT.CHANNELS.AUDIT.EMPTY') }}
        </div>
        <div v-else class="flex flex-col divide-y divide-n-weak">
          <div
            v-for="row in auditRows"
            :key="row.id"
            class="py-1.5 text-body-main text-n-slate-11"
          >
            <span class="opacity-65">{{ formatAuditDate(row.at) }}</span>
            {{ `— ${row.description}` }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
